import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { validateDocumentFiles } from "@/lib/validation";
import { extractSyncBolS3Error, formatSyncBolS3Error } from "@/lib/sync-bol-s3-error";

/**
 * Shared mutation for uploading Bill of Lading documents to an order.
 *
 * Two pipelines exist intentionally:
 *   - "driver"     → bol-images bucket.
 *   - "dispatcher" → idp-uploads bucket.
 * Neither mode creates document_extractions records.
 *
 * Both follow the Sync-then-Persist contract: the NetSuite-facing S3 sync
 * must succeed BEFORE local storage / DB writes. On any per-file local
 * failure the S3 object is rolled back so retries never duplicate.
 *
 * Multi-file batches process sequentially. Per-file failures are surfaced
 * but do NOT abort the batch — succeeding files are still committed.
 */
export type BolUploadMode = "driver" | "dispatcher";

export interface UseBolUploadParams {
  orderId: string;
  /** Human-readable order id (e.g. AOSC-DETM-00001) used as the S3 tag. */
  orderUniqueId: string;
  mode: BolUploadMode;
  /** Display name written to order_change_log. Falls back to user email. */
  changedBy?: string;
}

export interface BolUploadResult {
  uploaded: number;
  failed: number;
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1] || result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function useBolUpload({
  orderId,
  orderUniqueId,
  mode,
  changedBy,
}: UseBolUploadParams) {
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { activeTenant, tenantId } = useTenant();

  const invalidate = useCallback(() => {
    // Two key conventions exist across surfaces; invalidate both so every
    // reader (driver card, order detail, sheet, viewer dialog) refreshes.
    queryClient.invalidateQueries({ queryKey: ["bol-images", orderId] });
    queryClient.invalidateQueries({ queryKey: ["bol-images-count", orderId] });
    queryClient.invalidateQueries({ queryKey: ["bol-count", orderId] });
    queryClient.invalidateQueries({ queryKey: ["bol-extractions", orderId] });
  }, [queryClient, orderId]);

  const uploadOne = useCallback(
    async (file: File): Promise<void> => {
      let s3KeyForRollback: string | null = null;
      let storagePathForRollback: string | null = null;
      const bucket = mode === "driver" ? "bol-images" : "idp-uploads";

      try {
        // Step 1: Sync-then-Persist — S3 push MUST succeed first.
        const base64 = await fileToBase64(file);
        const { data: syncResult, error: syncError } =
          await supabase.functions.invoke("sync-bol-s3", {
            body: {
              file_data: base64,
              file_name: file.name,
              tenant_slug: activeTenant,
              order_id: orderUniqueId,
              doc_kind: "bol",
            },
          });
        if (syncError || !syncResult?.success) {
          const detail = await extractSyncBolS3Error(syncError, syncResult);
          throw new Error(`S3 upload failed: ${formatSyncBolS3Error(detail)}`);
        }
        s3KeyForRollback = syncResult?.s3_key ?? null;

        // Step 2: Storage upload.
        const fileExt = file.name.split(".").pop()?.toLowerCase() || "jpg";
        const filePath =
          mode === "driver"
            ? `${orderId}/${Date.now()}-${Math.random()
                .toString(36)
                .substring(7)}.${fileExt}`
            : `${user?.id ?? "anon"}/${Date.now()}-${Math.random()
                .toString(36)
                .substring(7)}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from(bucket)
          .upload(filePath, file, {
            cacheControl: "3600",
            upsert: false,
            contentType: file.type || undefined,
          });
        if (uploadError) throw uploadError;
        storagePathForRollback = filePath;

        // Step 3: Attach to order.
        const attachPayload: Record<string, unknown> = {
          order_id: orderId,
          image_url: filePath,
          image_path: filePath,
          s3_key: s3KeyForRollback,
          file_name: file.name,
        };
        if (tenantId) attachPayload.tenant_id = tenantId;
        const { error: insertError } = await supabase
          .from("order_bol_images")
          .insert(attachPayload as never);
        if (insertError) throw insertError;
      } catch (perFileErr) {
        // Rollback S3 first (billing-critical) then storage object.
        if (s3KeyForRollback) {
          try {
            await supabase.functions.invoke("sync-bol-s3", {
              body: {
                action: "delete",
                tenant_slug: activeTenant,
                order_id: orderUniqueId,
                s3_key: s3KeyForRollback,
              },
            });
          } catch (cleanupErr) {
            console.error(
              "S3 rollback failed for key",
              s3KeyForRollback,
              cleanupErr,
            );
            toast({
              title: "S3 cleanup failed",
              description: `Manual review needed for s3_key=${s3KeyForRollback}`,
              variant: "destructive",
            });
          }
        }
        if (storagePathForRollback) {
          await supabase.storage
            .from(bucket)
            .remove([storagePathForRollback])
            .catch(() => undefined);
        }
        throw perFileErr;
      }
    },
    [
      mode,
      orderId,
      orderUniqueId,
      activeTenant,
      tenantId,
      user?.id,
      user?.email,
      toast,
    ],
  );

  const logChange = useCallback(
    async (uploadedCount: number) => {
      const by = changedBy || user?.email || "User";
      await supabase.from("order_change_log").insert({
        order_id: orderId,
        field_name: "bill_of_lading",
        old_value: null,
        new_value: `${uploadedCount} photo${uploadedCount > 1 ? "s" : ""} uploaded`,
        changed_by: by,
      });
    },
    [changedBy, user?.email, orderId],
  );

  const uploadFiles = useCallback(
    async (files: FileList | File[]): Promise<BolUploadResult> => {
      const { valid, errors } = validateDocumentFiles(files);
      errors.forEach((e) =>
        toast({
          title: "Invalid file",
          description: e,
          variant: "destructive",
        }),
      );
      if (valid.length === 0) return { uploaded: 0, failed: 0 };

      setIsUploading(true);
      let uploaded = 0;
      let failed = 0;
      try {
        for (const file of valid) {
          try {
            await uploadOne(file);
            uploaded++;
          } catch (err) {
            failed++;
            console.error("BoL upload failed for", file.name, err);
            toast({
              title: "Upload failed",
              description: `${file.name}: ${(err as Error).message || "Unknown error"}`,
              variant: "destructive",
            });
          }
        }

        if (uploaded > 0) {
          await logChange(uploaded).catch((e) =>
            console.warn("order_change_log insert failed", e),
          );
          toast({
            title: "Bill of Lading uploaded",
            description: `${uploaded} photo${uploaded > 1 ? "s" : ""} uploaded successfully`,
          });
          invalidate();
        }
      } finally {
        setIsUploading(false);
      }
      return { uploaded, failed };
    },
    [uploadOne, logChange, invalidate, toast],
  );

  return { uploadFiles, isUploading };
}
